import { APP_URL } from "./config";
import React from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Zap,
  Lock,
  CheckCircle,
  ArrowRight,
  Star,
  Globe
} from "lucide-react";

/* Custom Button */
const Button = ({ children, className = "", ...props }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.96 }}
      transition={{ duration: 0.2 }}
      className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg ${className}`}
      {...props}
    >
      {children}
    </motion.button>
  );
};

function LandingPage() {
  const handleFeatureClick = () => {
    alert("🚧 Feature Coming Soon!");
  };

  const features = [
    {
      icon: Shield,
      title: "Post-Quantum Security",
      description:
        "Revolutionary cryptographic algorithms that remain secure against quantum computer attacks"
    },
    {
      icon: Zap,
      title: "AI-Powered Intelligence",
      description:
        "Advanced AI algorithms optimize signature processes and detect potential security threats"
    },
    {
      icon: Lock,
      title: "Next-Gen Encryption",
      description:
        "Military-grade encryption designed to protect your documents for the next 50 years"
    },
    {
      icon: Globe,
      title: "Global Compliance",
      description:
        "Meets international standards and regulations across all major jurisdictions"
    }
  ];

  const stats = [
    { number: "99.9%", label: "Uptime Guarantee" },
    { number: "50+", label: "Years Future-Proof" },
    { number: "256-bit", label: "Quantum Encryption" },
    { number: "24/7", label: "Expert Support" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 text-white overflow-hidden">
      {/* Navbar */}
      <nav className="px-6 py-4 relative z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2"
          >
            <Shield className="text-cyan-400 w-8 h-8" />
            <h1 className="text-2xl font-bold text-cyan-300">I-PQSign</h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden md:flex gap-6 items-center"
          >
            <button onClick={handleFeatureClick}>Features</button>
            <button onClick={handleFeatureClick}>Security</button>
            <button onClick={handleFeatureClick}>Pricing</button>
            <button onClick={handleFeatureClick}>Contact</button>

            <Button
              onClick={handleFeatureClick}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              Get Started
            </Button>
          </motion.div>
        </div>
      </nav>

      {/* Hero */}
      <section className="text-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/20 border border-cyan-400 mb-6">
            <Star className="w-4 h-4 text-cyan-300" />
            <span className="text-sm">
              World's First AI-Powered Post-Quantum eSignature
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold leading-tight">
            The Future of <br />
            <span className="text-cyan-400">Digital Trust</span>
          </h1>

          <p className="mt-6 text-xl text-gray-300 max-w-3xl mx-auto">
            Meet I-PQSign: The world’s first AI-powered, post-quantum
            eSignature platform built to secure digital trust for the next 50
            years.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Button
              onClick={() =>
                (window.location.href = `${APP_URL}/login`)
              }
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-lg px-8 py-4"
            >
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5 inline" />
            </Button>

            <Button
              onClick={handleFeatureClick}
              className="border border-cyan-400 hover:bg-cyan-500/10"
            >
              Watch Demo
            </Button>
          </div>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="mt-16 relative"
        >
          <div className="relative mx-auto max-w-4xl">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-2xl blur-3xl"></div>
            <img
              className="relative z-10 w-full rounded-2xl shadow-2xl border border-cyan-500/30"
              src="https://images.unsplash.com/photo-1651505942687-efc26cb528ba"
              alt="dashboard"
            />
          </div>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="px-6 py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              whileHover={{ y: -5 }}
              className="text-center"
            >
              <div className="text-4xl font-bold text-cyan-400">
                {stat.number}
              </div>
              <div className="text-gray-400 mt-2">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-cyan-400">
              Revolutionary Features
            </h2>
            <p className="mt-4 text-gray-300 text-xl">
              Experience the next generation of digital signatures with
              cutting-edge technology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                whileHover={{ y: -8 }}
                className="bg-slate-800/50 border border-cyan-500/20 rounded-2xl p-8"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>

                <h3 className="text-xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
          >
            <h2 className="text-5xl font-bold text-cyan-400 mb-6">
              Quantum-Proof Security
            </h2>

            <p className="text-xl text-gray-300 mb-8">
              Built with advanced post-quantum cryptography to ensure your
              documents remain secure even against future quantum computing
              threats.
            </p>

            <div className="space-y-4">
              {[
                "NIST-approved post-quantum algorithms",
                "AI-powered threat detection",
                "Zero-knowledge architecture",
                "Immutable audit trails"
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <CheckCircle className="text-cyan-400 w-5 h-5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={handleFeatureClick}
              className="mt-8 bg-gradient-to-r from-cyan-500 to-purple-600"
            >
              Learn More About Security
              <ArrowRight className="ml-2 w-4 h-4 inline" />
            </Button>
          </motion.div>

          <motion.img
            whileHover={{ scale: 1.03 }}
            className="rounded-2xl shadow-2xl border border-cyan-500/30"
            src="https://images.unsplash.com/photo-1651505942687-efc26cb528ba"
            alt="security"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20">
        <div className="max-w-4xl mx-auto text-center bg-slate-800/50 rounded-3xl p-12 border border-cyan-500/20">
          <h2 className="text-5xl font-bold text-cyan-400 mb-6">
            Ready for the Future?
          </h2>

          <p className="text-xl text-gray-300 mb-8">
            Join thousands of organizations already using I-PQSign.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-gradient-to-r from-cyan-500 to-purple-600">
              Start Your Free Trial
            </Button>

            <Button className="border border-cyan-500/50 text-cyan-300">
              Schedule Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
        <footer className="px-6 py-12 border-t border-slate-700/50">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    I-PQSign
                  </span>
                </div>
                <p className="text-gray-400">Powered by Intrust Innovation Labs</p>
              </div>
              
              <div>
                <span className="font-semibold text-white mb-4 block">Product</span>
                <div className="space-y-2">
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Features</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Security</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Integrations</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">API</button>
                </div>
              </div>
              
              <div>
                <span className="font-semibold text-white mb-4 block">Company</span>
                <div className="space-y-2">
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">About</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Careers</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Press</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Contact</button>
                </div>
              </div>
              
              <div>
                <span className="font-semibold text-white mb-4 block">Support</span>
                <div className="space-y-2">
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Help Center</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Documentation</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Status</button>
                  <button onClick={handleFeatureClick} className="block text-gray-400 hover:text-white transition-colors">Privacy</button>
                </div>
              </div>
            </div>
            
            <div className="border-t border-slate-700/50 mt-12 pt-8 text-center">
              <p className="text-gray-400">© 2025 Intrust Innovation Labs Private Limited. All rights reserved. Built for the next 50 years of digital trust.</p>
            </div>
          </div>
        </footer>
    </div>
  );
}

export default LandingPage;